import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search, Heart } from "lucide-react";
import { useState, useEffect } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, eur } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import logo from "@/assets/uvashop-logo.webp";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { count: wishCount } = useWishlist();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get("/wines", { params: { search: term } })
        .then((r) => setSuggestions(r.data.slice(0, 6)))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const goToWine = (id) => {
    setSearchOpen(false); setQ(""); setSuggestions([]);
    navigate(`/wine/${id}`);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/shop?search=${encodeURIComponent(term)}`);
    setSearchOpen(false);
    setSuggestions([]);
    setQ("");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 font-header text-white transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-ink/95 backdrop-blur-md border-b border-white/10" : "bg-ink/70 backdrop-blur-sm border-b border-white/5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <img src={logo} alt="UvaShop" width="749" height="186" className="h-7 lg:h-9 w-auto" />
        </Link>

        <nav aria-label="Navigazione principale" className="hidden md:flex items-center gap-8 text-[13px] font-600 tracking-[0.08em] uppercase">
          <Link to="/shop" data-testid="nav-shop" className="link-underline hover:text-white/70 transition-colors">Catalogo</Link>
          <div className="relative group">
            <Link to="/shop?type=sparkling" data-testid="nav-bollicine" className="link-underline hover:text-white/70 transition-colors">Bollicine</Link>
            <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-[opacity,transform] duration-200 z-50">
              <div className="bg-ink/95 backdrop-blur-md border border-white/10 min-w-[200px] py-2 shadow-2xl">
                <Link to="/shop?type=sparkling&method=charmat" data-testid="submenu-charmat" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Metodo Charmat</Link>
                <Link to="/shop?type=sparkling&method=classico" data-testid="submenu-classico" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Metodo Classico</Link>
              </div>
            </div>
          </div>
          <Link to="/shop?type=white" className="link-underline hover:text-white/70 transition-colors">Bianchi</Link>
          <Link to="/shop?type=red" className="link-underline hover:text-white/70 transition-colors">Rossi</Link>
          <div className="relative group">
            <Link to="/shop?type=distillati" data-testid="nav-distillati" className="link-underline hover:text-white/70 transition-colors">Distillati</Link>
            <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-[opacity,transform] duration-200 z-50">
              <div className="bg-ink/95 backdrop-blur-md border border-white/10 min-w-[210px] py-2 shadow-2xl">
                <Link to="/shop?type=distillati&spirit=gin" data-testid="submenu-gin" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Gin</Link>
                <Link to="/shop?type=distillati&spirit=rum" data-testid="submenu-rum" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Rum</Link>
                <Link to="/shop?type=distillati&spirit=whisky" data-testid="submenu-whisky" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Whisky</Link>
                <Link to="/shop?type=distillati&spirit=amari" data-testid="submenu-amari" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Amari e miscelati</Link>
                <Link to="/shop?type=distillati&spirit=grappe" data-testid="submenu-grappe" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Grappe</Link>
                <Link to="/shop?type=distillati&spirit=altro" data-testid="submenu-altro" className="block px-5 py-3 text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors">Altro</Link>
              </div>
            </div>
          </div>
          <Link to="/promo" data-testid="nav-promo" className="link-underline text-[#D1495B] hover:text-[#E76A7A] transition-colors">Promo</Link>
          <Link to="/gift-card" data-testid="nav-giftcard" className="link-underline hover:text-white/70 transition-colors">Gift Card</Link>
          <Link to="/blog" data-testid="nav-blog" className="link-underline hover:text-white/70 transition-colors">Blog</Link>
          <Link to="/aziende" data-testid="nav-aziende" className="link-underline hover:text-white/70 transition-colors">Aziende</Link>
        </nav>

        <div className="flex items-center gap-5">
          {user ? (
            <div className="hidden md:flex items-center gap-4 text-[13px] font-600 tracking-[0.08em]">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="link-underline uppercase">Dashboard</Link>
              )}
              <Link to="/account" data-testid="nav-account" className="flex items-center gap-2 link-underline uppercase">
                {user.picture && (
                  <img src={user.picture} alt="" referrerPolicy="no-referrer" data-testid="header-avatar" className="w-7 h-7 rounded-full object-cover border border-white/25" />
                )}
                {user.name.split(" ")[0]}
              </Link>
              <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="text-white/60 hover:text-claret transition-colors uppercase">Esci</button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" aria-label="Accedi al tuo account" className="hidden md:block hover:text-white/70 transition-colors">
              <User size={20} strokeWidth={1.5} />
            </Link>
          )}
          <Link to={user ? "/account" : "/login"} data-testid="nav-user-mobile" aria-label={user ? "Il mio account" : "Accedi o registrati"} className="md:hidden hover:text-white/70 transition-colors">
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" data-testid="header-avatar-mobile" className="w-7 h-7 rounded-full object-cover border border-white/25" />
            ) : (
              <User size={20} strokeWidth={1.5} />
            )}
          </Link>
          <Link to="/preferiti" data-testid="nav-wishlist" aria-label={`Preferiti${wishCount > 0 ? `, ${wishCount} salvati` : ""}`} className="relative hover:text-white/70 transition-colors hidden md:block">
            <Heart size={20} strokeWidth={1.5} />
            {wishCount > 0 && (
              <span data-testid="wishlist-count" className="absolute -top-2 -right-2 bg-claret text-bone text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{wishCount}</span>
            )}
          </Link>
          <button data-testid="cart-toggle" onClick={() => setOpen(true)} aria-label={`Carrello${count > 0 ? `, ${count} articoli` : ""}`} className="relative">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-claret text-bone text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
            )}
          </button>
          <button data-testid="search-toggle" onClick={() => setSearchOpen((o) => !o)} aria-label="Cerca" aria-expanded={searchOpen} className="hover:text-white/70 transition-colors">
            <Search size={20} strokeWidth={1.5} />
          </button>
          <button className="md:hidden" onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle" aria-label={menu ? "Chiudi menu" : "Apri menu"} aria-expanded={menu}>
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-white/10 bg-ink/95 backdrop-blur-md" data-testid="header-search-panel">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-3">
            <Search size={18} strokeWidth={1.5} className="text-white/60 shrink-0" />
            <input
              autoFocus
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca vini, cantine, regioni…"
              className="flex-1 bg-transparent outline-none text-white placeholder-white/45 py-2 text-base font-sans"
            />
            <button type="submit" data-testid="header-search-submit" className="text-[12px] uppercase tracking-[0.1em] font-600 text-white/80 hover:text-white transition-colors">Cerca</button>
            <button type="button" onClick={() => setSearchOpen(false)} className="text-white/60 hover:text-white transition-colors" aria-label="Chiudi ricerca">
              <X size={18} />
            </button>
          </form>
          {suggestions.length > 0 && (
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-4" data-testid="search-suggestions">
              <ul className="divide-y divide-white/10 border-t border-white/10">
                {suggestions.map((w) => (
                  <li key={w.id}>
                    <button type="button" onClick={() => goToWine(w.id)} data-testid={`search-suggestion-${w.id}`} className="w-full flex items-center gap-4 py-3 text-left hover:bg-white/[0.04] transition-colors px-2">
                      <img src={w.image} alt="" className="w-10 h-12 object-cover bg-white/5 shrink-0" loading="lazy" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-white text-sm truncate">{w.name}</span>
                        <span className="block text-white/50 text-xs truncate">{[w.producer, w.region].filter(Boolean).join(" · ")}</span>
                      </span>
                      <span className="text-white/80 text-sm shrink-0">{eur(w.price)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {menu && (
        <div className="md:hidden bg-ink text-white border-t border-white/10 px-6 py-6 flex flex-col gap-4 text-sm uppercase tracking-wide font-header font-600">
          <Link to="/shop" onClick={() => setMenu(false)}>Catalogo</Link>
          <Link to="/shop?type=sparkling" onClick={() => setMenu(false)}>Bollicine</Link>
          <Link to="/shop?type=sparkling&method=charmat" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Metodo Charmat</Link>
          <Link to="/shop?type=sparkling&method=classico" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Metodo Classico</Link>
          <Link to="/shop?type=white" onClick={() => setMenu(false)}>Bianchi</Link>
          <Link to="/shop?type=red" onClick={() => setMenu(false)}>Rossi</Link>
          <Link to="/shop?type=distillati" onClick={() => setMenu(false)}>Distillati</Link>
          <Link to="/shop?type=distillati&spirit=gin" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Gin</Link>
          <Link to="/shop?type=distillati&spirit=rum" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Rum</Link>
          <Link to="/shop?type=distillati&spirit=whisky" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Whisky</Link>
          <Link to="/shop?type=distillati&spirit=amari" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Amari e miscelati</Link>
          <Link to="/shop?type=distillati&spirit=grappe" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Grappe</Link>
          <Link to="/shop?type=distillati&spirit=altro" className="pl-4 text-[13px] text-white/60" onClick={() => setMenu(false)}>— Altro</Link>
          <Link to="/promo" onClick={() => setMenu(false)} className="text-[#D1495B]">Promo</Link>
          <Link to="/gift-card" onClick={() => setMenu(false)}>Gift Card</Link>
          <Link to="/blog" onClick={() => setMenu(false)}>Blog</Link>
          <Link to="/aziende" onClick={() => setMenu(false)}>Aziende</Link>
          {user ? (
            <>
              <Link to="/account" onClick={() => setMenu(false)}>Il mio account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setMenu(false)}>Dashboard</Link>}
              <button className="text-left" onClick={() => { logout(); setMenu(false); navigate("/"); }}>Esci</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setMenu(false)}>Accedi</Link>
          )}
        </div>
      )}
    </header>
  );
}
